import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "lib/utils";
import type { ChangeEvent } from "react";

const rangeVariants = cva("range", {
  variants: {
    size: {
      xs: "range-xs",
      sm: "range-sm",
      md: "range-md",
      lg: "range-lg",
    },
    color: {
      default: "",
      primary: "range-primary",
      secondary: "range-secondary",
      info: "range-info",
      accent: "range-accent",
      success: "range-success",
      warning: "range-warning",
      error: "range-error",
    },
  },
  defaultVariants: {
    size: "md",
    color: "default",
  },
});

type RangeVariantProps = VariantProps<typeof rangeVariants>;

export type FromToRangeProps = {
  min: number;
  max: number;
  step: number;
  fromValue?: number;
  toValue?: number;
  onFromChange?: (value: number) => void;
  onToChange?: (value: number) => void;
  unitName?: string;
  testLabel?: string;
  size?: RangeVariantProps["size"];
  color?: RangeVariantProps["color"];
};

/**
 * 純粋な FromTo の Range リーフ。from/to 2本のスライダーと表示ラベル。
 * @param {FromToRangeProps} props FromToRange の props
 * @return {JSX.Element} Range UI
 */
export const FromToRange = ({
  min,
  max,
  step,
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  unitName = "",
  testLabel = "",
  size,
  color,
}: FromToRangeProps): JSX.Element => {
  const handleFrom = (e: ChangeEvent<HTMLInputElement>) =>
    onFromChange?.(Number(e.target.value));
  const handleTo = (e: ChangeEvent<HTMLInputElement>) =>
    onToChange?.(Number(e.target.value));

  const rangeClass = cn(rangeVariants({ size, color }));

  return (
    <div className="w-full">
      <input
        data-testid={testLabel ? `${testLabel}-from` : undefined}
        type="range"
        min={min}
        max={max}
        step={step}
        value={fromValue ?? min}
        onChange={handleFrom}
        className={rangeClass}
      />
      <label className="label mb-2">
        <span className="label-text-alt">
          <span className="text-xl font-bold">{fromValue}</span>
          {unitName}
        </span>
        <span className="label-text-alt">〜</span>
        <span className="label-text-alt">
          <span className="text-xl font-bold">{toValue}</span>
          {unitName}
        </span>
      </label>
      <input
        data-testid={testLabel ? `${testLabel}-to` : undefined}
        type="range"
        min={min}
        max={max}
        step={step}
        value={toValue ?? max}
        onChange={handleTo}
        className={rangeClass}
      />
    </div>
  );
};
